import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface BillDetailsData {
  id: number;
  project_name: string;
  project_type: string;
  domain: string;
  sub_domain?: string;
  area: number;
  quantity: number;
  unit_rate: number;
  bill_amount: number;
  rate_type: string;
  base_rate: number;
  status: string;
  billing_date: string;
  payment_date: string;
  invoice_number: string;
  created_at: string;
  updated_at?: string;
  org_name?: string;
  org_address?: string;
  org_gst?: string;
  org_pan?: string;
  org_email?: string;
  org_phone?: string;
  client_name?: string;
  road_length?: number;
  lane_configuration?: number;
  project_road_length?: number;
  metadata?: any;
  amount_history?: any[];
  last_amount_update?: any;
}

@Component({
  selector: 'app-bill-details-dialog',
  templateUrl: './bill-details-dialog.component.html',
  styleUrls: ['./bill-details-dialog.component.css']
})
export class BillDetailsDialogComponent implements OnInit {
  bill: BillDetailsData;

  constructor(
    public dialogRef: MatDialogRef<BillDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BillDetailsData
  ) {
    this.bill = data;
  }

  ngOnInit(): void {
    // Ensure numeric values are properly parsed from strings
    if (this.bill) {
      this.bill.area = parseFloat(this.bill.area?.toString() || '0') || 0;
      this.bill.quantity = parseFloat(this.bill.quantity?.toString() || '0') || 0;
      this.bill.unit_rate = parseFloat(this.bill.unit_rate?.toString() || '0') || 0;
      this.bill.bill_amount = parseFloat(this.bill.bill_amount?.toString() || '0') || 0;
      this.bill.base_rate = parseFloat(this.bill.base_rate?.toString() || '0') || 0;
      this.bill.road_length = parseFloat(this.bill.road_length?.toString() || '0') || null;
      this.bill.project_road_length = parseFloat(this.bill.project_road_length?.toString() || '0') || null;
      if (this.bill.lane_configuration) {
        this.bill.lane_configuration = parseFloat(this.bill.lane_configuration.toString());
      }
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'billed': 'bg-blue-100 text-blue-800',
      'paid': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  getProjectTypeLabel(type: string): string {
    const typeLabels: { [key: string]: string } = {
      'CONSTRUCTION': 'Construction',
      'ROAD': 'Road',
      'UTILITIES': 'Utilities',
      'CONSULTANCY': 'Consultancy'
    };
    return typeLabels[type] || type;
  }

  formatQuantity(quantity: number, rateType: string): string {
    if (rateType === 'per_project') {
      return '1 Project';
    }
    const qty = parseFloat(quantity?.toString() || '0') || 0;
    return `${qty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} sqm`;
  }

  /**
   * Calculate number of lanes from quantity and road length
   * Formula: quantity = 3.75 * numberOfLanes * roadLength
   */
  calculateNumberOfLanes(): number | null {
    if (this.bill.project_type !== 'ROAD') {
      return null;
    }
    
    // Use lane_configuration if available
    if (this.bill.lane_configuration) {
      return parseFloat(this.bill.lane_configuration.toString());
    }
    
    // Calculate from quantity and road_length
    const roadLength = parseFloat(this.bill.road_length?.toString() || this.bill.project_road_length?.toString() || '0');
    const quantity = parseFloat(this.bill.quantity?.toString() || '0');
    
    if (roadLength > 0 && quantity > 0) {
      const laneWidth = 3.75; // meters per lane
      const calculatedLanes = quantity / (laneWidth * roadLength);
      return Math.round(calculatedLanes * 100) / 100; // Round to 2 decimal places
    }
    
    return null;
  }

  /**
   * Get road length for display
   */
  getRoadLength(): number | null {
    // Try road_length first, then project_road_length
    let roadLength = 0;
    if (this.bill.road_length) {
      roadLength = parseFloat(this.bill.road_length.toString());
    } else if (this.bill.project_road_length) {
      roadLength = parseFloat(this.bill.project_road_length.toString());
    }
    return roadLength > 0 ? roadLength : null;
  }

  /**
   * Format road length with unit
   */
  formatRoadLength(): string {
    const length = this.getRoadLength();
    if (!length) return 'N/A';
    return `${length.toLocaleString('en-IN', { maximumFractionDigits: 2 })} m`;
  }

  /**
   * Get amount change history from metadata
   */
  getAmountHistory(): any[] {
    if (this.bill.metadata) {
      try {
        const metadata = typeof this.bill.metadata === 'string' 
          ? JSON.parse(this.bill.metadata) 
          : this.bill.metadata;
        return metadata.amount_history || [];
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  /**
   * Calculate original bill amount based on quantity and unit rate
   */
  getCalculatedAmount(): number {
    const qty = parseFloat(this.bill.quantity?.toString() || '0') || 0;
    const rate = parseFloat(this.bill.unit_rate?.toString() || '0') || 0;
    return qty * rate;
  }

  /**
   * Check if bill amount was manually updated
   */
  isAmountManuallyUpdated(): boolean {
    const calculated = this.getCalculatedAmount();
    const current = parseFloat(this.bill.bill_amount?.toString() || '0') || 0;
    return Math.abs(calculated - current) > 0.01; // Allow for small floating point differences
  }

  /**
   * Format date for display
   */
  formatDateTime(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}

